import React from "react";
import { Home, Building2, Trees, ClipboardCheck, Check, ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { mockData, media } from "../mock";

const getIcon = (title) => {
  if (title.includes("Residential")) return <Home className="h-7 w-7" />;
  if (title.includes("Commercial")) return <Building2 className="h-7 w-7" />;
  if (title.includes("Site")) return <Trees className="h-7 w-7" />;
  return <ClipboardCheck className="h-7 w-7" />;
};

export const Services = () => {
  const navigate = useNavigate();

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-20 md:py-28 overflow-hidden"
    >
      <div className="absolute inset-0 tex-grid opacity-60" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-14">
          <p className="label-kicker mb-4">What We Do</p>
          <h2 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-tight text-white leading-none">
            Full-Service
            <span className="text-primary"> Demolition</span>
          </h2>
          <p className="mt-5 text-gray-400 text-lg">
            From the first permit to the final swept slab — we provide
            comprehensive demolition and site work with the highest safety
            standards in the industry.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border">
          {mockData.services.map((service, idx) => (
            <div
              key={service.id}
              data-testid={`service-card-${service.id}`}
              className="group bg-card p-8 lg:p-10 transition-colors duration-300 hover:bg-surface-alt relative"
            >
              <div className="flex items-start justify-between mb-6">
                <div className="w-14 h-14 flex items-center justify-center border border-primary/30 bg-primary/10 text-primary group-hover:bg-primary group-hover:text-background transition-colors duration-300">
                  {getIcon(service.title)}
                </div>
                <span className="font-display text-5xl font-black text-white/5 group-hover:text-primary/20 transition-colors">
                  0{idx + 1}
                </span>
              </div>
              <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-white mb-3">
                {service.title}
              </h3>
              <p className="text-gray-400 leading-relaxed mb-6">
                {service.description}
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                {service.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                    <Check className="h-4 w-4 text-primary flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* CTA band */}
        <div className="relative mt-14 border border-border overflow-hidden">
          <img
            src={media.servicesImg}
            alt="Excavator demolishing a structure"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-background/80" />
          <div className="relative flex flex-col md:flex-row items-center justify-between gap-6 p-10 md:p-14">
            <div>
              <h3 className="font-display text-3xl md:text-4xl font-bold uppercase tracking-tight text-white">
                Ready to break ground?
              </h3>
              <p className="text-gray-300 mt-2">
                Get a free, no-obligation quote for your project today.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                data-testid="services-quote-btn"
                size="lg"
                onClick={() => navigate("/quote")}
                className="rounded-none font-bold uppercase tracking-widest px-8 py-6"
              >
                Request Quote
                <ArrowUpRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                data-testid="services-all-btn"
                size="lg"
                variant="outline"
                onClick={() => navigate("/services")}
                className="rounded-none font-bold uppercase tracking-widest px-8 py-6 border-white/30 bg-transparent text-white hover:bg-white hover:text-background"
              >
                All Services
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
